'use client'

import { createContext, useContext, useCallback, useEffect, useState } from 'react'
import {
  type UIProfile,
  DEFAULT_PROFILE,
  PROFILE_DEFINITIONS,
} from '@/lib/ui-profiles'
import { getProfileTileStyles, type ProfileTileStyle } from '@/lib/profile-tiles'
import { useTheme } from './ThemeProvider'

interface ProfileContextValue {
  profile: UIProfile
  setProfile: (p: UIProfile) => void
}

const ProfileContext = createContext<ProfileContextValue>({
  profile: DEFAULT_PROFILE,
  setProfile: () => {},
})

export function useProfile() {
  return useContext(ProfileContext)
}

/**
 * Convenience hook: returns the tile styling for the active profile.
 * Use this in any page that renders navigation tiles or module cards.
 *
 * @example
 *   const tileStyle = useProfileTileStyle()
 *   <div style={{ ...tileStyle.container, padding: '1.5rem' }}>
 *     <h3 style={tileStyle.title}>Title</h3>
 *   </div>
 */
export function useProfileTileStyle(): ProfileTileStyle {
  const { profile } = useProfile()
  return getProfileTileStyles(profile)
}

function applyCookie(profile: UIProfile) {
  document.cookie = `gg-profile=${profile};path=/;max-age=31536000;samesite=lax`
}

function applyProfileToDoc(profile: UIProfile) {
  document.documentElement.setAttribute('data-profile', profile)
}

/** Apply CSS variable overrides from the profile definition */
function applyCSSOverrides(profile: UIProfile, theme: 'dark' | 'light') {
  const root = document.documentElement
  // First clear any prior profile overrides
  for (const def of Object.values(PROFILE_DEFINITIONS)) {
    const allOverrides = { ...def.cssOverrides.dark, ...def.cssOverrides.light }
    for (const key of Object.keys(allOverrides)) {
      root.style.removeProperty(key)
    }
  }
  // Then apply the active profile's overrides for the current theme
  const overrides = PROFILE_DEFINITIONS[profile]?.cssOverrides[theme] ?? {}
  for (const [key, value] of Object.entries(overrides)) {
    root.style.setProperty(key, value)
  }
}

interface ProfileProviderProps {
  initialProfile: UIProfile
  userEmail?: string | null
  children: React.ReactNode
}

export default function ProfileProvider({ initialProfile, userEmail, children }: ProfileProviderProps) {
  const [profile, setProfileState] = useState<UIProfile>(initialProfile)
  const { theme } = useTheme()

  // Sync to DOM whenever profile or theme changes
  useEffect(() => {
    applyProfileToDoc(profile)
    applyCSSOverrides(profile, theme)
  }, [profile, theme])

  // On mount: if user is known, load their saved profile from KV
  useEffect(() => {
    if (!userEmail) return
    fetch(`/api/user/profile?email=${encodeURIComponent(userEmail)}`)
      .then(r => r.json())
      .then(({ profile: saved }) => {
        if (saved && saved !== profile) {
          setProfileState(saved as UIProfile)
          applyProfileToDoc(saved as UIProfile)
          applyCSSOverrides(saved as UIProfile, theme)
          applyCookie(saved as UIProfile)
        }
      })
      .catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail])

  const setProfile = useCallback((p: UIProfile) => {
    setProfileState(p)
    applyProfileToDoc(p)
    applyCSSOverrides(p, theme)
    applyCookie(p)
    if (userEmail) {
      fetch('/api/user/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail, profile: p }),
      }).catch(() => {})
    }
  }, [userEmail, theme])

  return (
    <ProfileContext.Provider value={{ profile, setProfile }}>
      {children}
    </ProfileContext.Provider>
  )
}
